import React, { useEffect, useRef, useCallback } from 'react';
import type { FilteredViewCategory, ViewId } from '../config/navConfig';
import { NavViewGroup } from './NavViewGroup';
import { DashboardNavSubmenu } from './DashboardNavSubmenu';
import { useDashboardNavConfig } from '../hooks/useDashboardNavConfig';

interface AuthUser {
  sub: string;
  email: string;
  role: 'Admin' | 'Organizer' | 'Delegate' | 'Attendee';
  fullName?: string;
}

export interface MobileNavOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  navViews: FilteredViewCategory[];
  collapsedViews: Set<ViewId>;
  onToggleView: (viewId: ViewId) => void;
  user: AuthUser | null;
  onLogout: () => void;
}

/**
 * MobileNavOverlay — Full-screen slide-in navigation panel from the left.
 * Displays the same NavViewGroup components as the desktop sidebar for consistent behavior.
 * Closes on backdrop tap, nav link tap, or close button.
 * Traps keyboard focus within the overlay when open (accessibility).
 * Uses CSS transform for GPU-accelerated slide animation.
 */
export function MobileNavOverlay({
  isOpen,
  onClose,
  navViews,
  collapsedViews,
  onToggleView,
  user,
  onLogout,
}: MobileNavOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { config: dashboardNavConfig } = useDashboardNavConfig();

  // Focus trap: keep keyboard focus within the overlay when open
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Tab') {
      const overlay = overlayRef.current;
      if (!overlay) return;

      const focusableElements = overlay.querySelectorAll<HTMLElement>(
        'button, [href], a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const focusableArray = Array.from(focusableElements);
      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, onClose]);

  // Attach/detach keyboard listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Focus the close button when overlay opens
      setTimeout(() => closeButtonRef.current?.focus(), 50);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  function handleNavItemClick() {
    onClose();
  }

  function handleLogout() {
    onLogout();
    onClose();
  }

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          ...styles.backdrop,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          ...styles.panel,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        {/* Close button */}
        <div style={styles.panelHeader}>
          <div style={styles.panelBrand}>
            <img src="/KDULogo.PNG" alt="KDU" style={styles.logoImg} />
            <div>
              <div style={styles.brandTitle}>KDU Reunion Hub</div>
              <div style={styles.brandSub}>Georgia 2026</div>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            style={styles.closeButton}
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            ✕
          </button>
        </div>

        {/* User info and sign-out — placed at top for accessibility */}
        <div style={{ padding: '0 1rem 0.75rem', borderBottom: '1px solid #e5e7eb' }}>
          {user && (
            <div style={{ ...styles.userInfo, marginBottom: '0.5rem' }}>
              <div style={styles.avatar}>
                {user.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div style={styles.userDetails}>
                <div style={styles.userName}>
                  {user.fullName || user.email?.split('@')[0] || 'User'}
                </div>
                <div style={styles.userRole}>{user.role}</div>
              </div>
            </div>
          )}
          <button
            style={styles.signOutButton}
            onClick={handleLogout}
          >
            Sign Out
          </button>
        </div>

        {/* Navigation view groups */}
        <nav style={styles.nav}>
          {/* Dashboard Nav Submenu — at top when admin-enabled */}
          {dashboardNavConfig.enabled && (
            <DashboardNavSubmenu label={dashboardNavConfig.label} userRole={user?.role} />
          )}

          {navViews.map((viewCategory) => (
            <NavViewGroup
              key={viewCategory.category.id}
              view={viewCategory.category}
              items={viewCategory.items}
              isCollapsed={collapsedViews.has(viewCategory.category.id)}
              onToggle={() => onToggleView(viewCategory.category.id)}
              onItemClick={handleNavItemClick}
            />
          ))}
        </nav>

        {/* Footer spacer */}
        <div style={{ padding: '1rem' }} />
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 299,
    transition: 'opacity 0.3s ease',
  },
  panel: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '85vw',
    backgroundColor: '#0a1f14',
    zIndex: 300,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'transform',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  panelBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoImg: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    objectFit: 'contain' as const,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: '2px',
  },
  brandTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#f0f4f1',
    lineHeight: '1.2',
  },
  brandSub: {
    fontSize: '10px',
    color: '#c9a84c',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  closeButton: {
    minWidth: '44px',
    minHeight: '44px',
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '20px',
    color: '#f0f4f1',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  nav: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
  },
  footer: {
    padding: '16px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1a6b3c, #2a8f54)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    color: 'white',
    flexShrink: 0,
  },
  userDetails: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: '14px',
    color: '#f0f4f1',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  userRole: {
    fontSize: '11px',
    color: '#6b8572',
  },
  signOutButton: {
    width: '100%',
    minHeight: '44px',
    padding: '12px',
    backgroundColor: 'rgba(255,82,82,0.1)',
    border: '1px solid rgba(255,82,82,0.3)',
    borderRadius: '8px',
    color: '#ff8a80',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    WebkitTapHighlightColor: 'transparent',
  },
};
